import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Download, Upload, Image, LogOut, UserRound, Sparkles } from 'lucide-react';
import { useStore } from '../store/useStore.js';
import type { Theme } from '../store/types.js';
import { useLogout, useMe, useWorkspaces, type Workspace } from '../lib/queries.js';

interface Props {
  workspace: Workspace;
  onExport: () => void;
  onExportPng: () => void;
  onImport: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenCreateWs: () => void;
  onOpenShortcuts: () => void;
}

const THEMES: Array<{ key: Theme; label: string; color: string }> = [
  { key: 'dark',     label: 'Escuro',    color: '#00f5ff' },
  { key: 'teal',     label: 'Teal',      color: '#06b6d4' },
  { key: 'midnight', label: 'Midnight',  color: '#6478ff' },
  { key: 'forest',   label: 'Floresta',  color: '#00cc66' },
  { key: 'light',    label: 'Claro',     color: '#00a87a' },
];

function Icon({ name, size = 18 }: { name: string; size?: number }) {
  return (
    <span className="material-symbols-outlined" style={{ fontSize: size }}>
      {name}
    </span>
  );
}

export function Topbar({ workspace, onExport, onExportPng, onImport, onOpenCreateWs, onOpenShortcuts }: Props) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: wsData } = useWorkspaces();
  const logout = useLogout();

  const viewMode = useStore((s) => s.viewMode);
  const setViewMode = useStore((s) => s.setViewMode);
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const universeStack = useStore((s) => s.universeStack);
  const focusedParentId = useStore((s) => s.focusedParentId);
  const nodes = useStore((s) => s.nodes);
  const jumpToUniverse = useStore((s) => s.jumpToUniverse);
  const searchQuery = useStore((s) => s.searchQuery);
  const setSearch = useStore((s) => s.setSearch);
  const remoteCursors = useStore((s) => s.remoteCursors);
  const selectedId = useStore((s) => s.selectedId);
  const setSelected = useStore((s) => s.setSelected);

  const [showThemes, setShowThemes] = useState(false);
  const [showWs, setShowWs] = useState(false);
  const [showAcc, setShowAcc] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);
  const wsRef = useRef<HTMLDivElement>(null);
  const themeRef = useRef<HTMLDivElement>(null);
  const accRef = useRef<HTMLDivElement>(null);

  // Breadcrumbs
  type Crumb = { label: string; id: string | null; stackIdx: number };
  const crumbs: Crumb[] = [{ label: 'Córtex', id: null, stackIdx: -1 }];
  if (viewMode === 'list' && selectedId) {
    let curr = nodes.find((n) => n.id === selectedId);
    const path: typeof nodes = [];
    while (curr) { path.unshift(curr); curr = nodes.find((n) => n.id === curr!.parentId); }
    path.forEach((p) => crumbs.push({ label: p.label, id: p.id, stackIdx: -1 }));
  } else {
    universeStack.forEach((entry, i) => crumbs.push({ label: entry.label, id: entry.nodeId, stackIdx: i }));
    if (focusedParentId && !universeStack.length) {
      const node = nodes.find((n) => n.id === focusedParentId);
      if (node) crumbs.push({ label: node.label, id: node.id, stackIdx: -1 });
    }
  }

  function handleCrumb(c: Crumb) {
    if (viewMode === 'list') setSelected(c.id);
    else jumpToUniverse(c.stackIdx);
  }

  async function doLogout() {
    await logout.mutateAsync();
    navigate({ to: '/login' });
  }

  const initials = me?.name
    ? me.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    : me?.email?.[0]?.toUpperCase() ?? '?';

  return (
    <header className="syn-topbar">
      {/* Left */}
      <div className="syn-tb-left">
        {/* Workspace switcher */}
        <div ref={wsRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button className="syn-wmark" onClick={() => setShowWs((v) => !v)}>
            <span className="syn-logo">FLOWMINDY</span>
            <span className="syn-ws-sep">·</span>
            <span className="syn-ws-name">{workspace.name}</span>
            <Icon name="unfold_more" size={16} />
          </button>
          {showWs && (
            <div className="syn-pop">
              {wsData?.workspaces.map((w) => (
                <div
                  key={w.id}
                  className={'syn-pop-item' + (w.slug === workspace.slug ? ' is-active' : '')}
                  onClick={() => { setShowWs(false); navigate({ to: '/w/$slug', params: { slug: w.slug } }); }}
                >
                  <span className="dot" />
                  <span className="lbl">
                    <div className="title">{w.name}</div>
                    <div className="sub">/{w.slug}</div>
                  </span>
                  <span className="role">{w.role}</span>
                </div>
              ))}
              <div className="syn-pop-sep" />
              <button className="syn-pop-add" onClick={() => { setShowWs(false); onOpenCreateWs(); }}>
                <Icon name="add" size={16} /> Criar workspace
              </button>
            </div>
          )}
        </div>

        {/* Breadcrumb */}
        <nav className="syn-crumbs">
          {crumbs.map((c, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center' }}>
              {i > 0 && <span className="sep"><Icon name="chevron_right" size={14} /></span>}
              <button
                className={'seg' + (i === crumbs.length - 1 ? ' active' : '')}
                onClick={() => handleCrumb(c)}
              >
                {c.label}
              </button>
            </span>
          ))}
        </nav>
      </div>

      {/* Center — view switcher */}
      <div className="syn-tb-center">
        <div className={'syn-switcher' + (viewMode === 'list' ? ' is-docs' : '')}>
          <span className="syn-switcher-ind" />
          <button className={viewMode === 'graph' ? 'active' : ''} onClick={() => setViewMode('graph')}>
            <Icon name="hub" size={14} />
            Flow
          </button>
          <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>
            <Icon name="article" size={14} />
            Documentos
          </button>
        </div>
      </div>

      {/* Right */}
      <div className="syn-tb-right">
        {/* Theme picker */}
        <div ref={themeRef} style={{ position: 'relative' }}>
          <button
            className={'syn-iconbtn' + (showThemes ? ' is-active' : '')}
            title="Temas"
            onClick={() => setShowThemes((v) => !v)}
          >
            <Icon name="palette" size={18} />
            <span className="syn-tip">Temas</span>
          </button>
          {showThemes && (
            <div className="syn-pop align-right" style={{ minWidth: 180 }}>
              {THEMES.map((t) => (
                <div
                  key={t.key}
                  className={'syn-pop-item' + (theme === t.key ? ' is-active' : '')}
                  onClick={() => { setTheme(t.key); setShowThemes(false); }}
                >
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%', background: t.color,
                    boxShadow: `0 0 8px ${t.color}88`, flexShrink: 0,
                    border: '.5px solid rgba(255,255,255,.15)',
                  }} />
                  <span className="lbl"><div className="title">{t.label}</div></span>
                  {theme === t.key && <Icon name="check" size={14} />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Export buttons */}
        <button className="syn-iconbtn tb-hideable" title="Exportar PNG" onClick={onExportPng}>
          <Image size={16} />
          <span className="syn-tip">Exportar PNG</span>
        </button>
        <button className="syn-iconbtn tb-hideable" title="Exportar JSON" onClick={onExport}>
          <Download size={16} />
          <span className="syn-tip">Exportar JSON <span className="tip-kbd">⌘E</span></span>
        </button>
        <button className="syn-iconbtn tb-hideable" title="Importar JSON" onClick={() => importRef.current?.click()}>
          <Upload size={16} />
          <span className="syn-tip">Importar JSON</span>
        </button>
        <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={onImport} />

        <div className="syn-divider-v" />

        <button
          className="syn-upgrade-btn"
          onClick={() => navigate({ to: '/billing' })}
          title="Ver planos e fazer upgrade"
        >
          <Sparkles size={13} />
          <span>Upgrade</span>
        </button>

        <div className="syn-divider-v" />

        {/* Account */}
        <div ref={accRef} style={{ position: 'relative' }}>
          <button className="syn-avatar" onClick={() => setShowAcc((v) => !v)} title={me?.email ?? ''} style={{ overflow: 'hidden', padding: 0 }}>
            {(me as any)?.image ? (
              <img
                src={`/api/auth/avatar/${me!.id}`}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : initials}
          </button>
          {showAcc && (
            <div className="syn-pop align-right" style={{ minWidth: 220 }}>
              <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--muted)' }}>{me?.email}</div>
              {me?.admin && <div style={{ padding: '2px 10px 8px', fontSize: 10, color: 'var(--neon)' }}>{me.admin}</div>}
              <div className="syn-pop-sep" />
              <div className="syn-pop-item" onClick={() => { setShowAcc(false); navigate({ to: '/profile' }); }}>
                <UserRound size={14} />
                <span className="lbl"><div className="title">Perfil</div></span>
              </div>
              <div className="syn-pop-item" onClick={() => { setShowAcc(false); navigate({ to: '/billing' }); }}>
                <Sparkles size={14} />
                <span className="lbl"><div className="title">Planos & Assinatura</div></span>
              </div>
              <div className="syn-pop-item" onClick={() => { setShowAcc(false); onOpenShortcuts(); }}>
                <Icon name="keyboard" size={14} />
                <span className="lbl"><div className="title">Atalhos de teclado</div></span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted)' }}>?</span>
              </div>
              <div className="syn-pop-item" onClick={doLogout} style={{ color: '#f87171' }}>
                <LogOut size={14} />
                <span className="lbl"><div className="title" style={{ color: '#f87171' }}>Sair</div></span>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
